import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.clipPath
import androidx.compose.ui.graphics.lerp
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.Dp
import banko.composeapp.generated.resources.Res
import banko.composeapp.generated.resources.dice_section_base
import org.jetbrains.compose.resources.stringResource
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.pow
import kotlin.math.sin

/** Shared cosmetic renderer: cube materials and flame geometry follow the result-preserving tumble. */
@Composable
internal fun Dice3D(
    number: Int,
    size: Dp,
    rollProgress: Float,
    variant: Int,
    modifier: Modifier = Modifier,
    skin: DiceSkinStyle = DiceSkin.DEFAULT.style(),
    hat: DiceHat = DiceHat.NONE,
    animated: Boolean = true,
) {
    val diceLabel = stringResource(Res.string.dice_section_base)
    var phase by remember { mutableFloatStateOf(0.7f) }
    val hasFire = skin.innerFire != null || hat.palette != null
    LaunchedEffect(hasFire, animated) {
        if (!hasFire || !animated) return@LaunchedEffect
        var start = 0L
        var last = 0L
        while (true) withFrameNanos { now ->
            if (start == 0L) start = now
            if (now - last >= 33_000_000L) {
                phase = ((now - start) % 1_000_000_000_000L) / 1_000_000_000f
                last = now
            }
        }
    }
    Canvas(modifier.size(size).semantics { contentDescription = "$diceLabel: $number" }) {
        drawDice3D(number, rollProgress, variant, skin, hat, phase)
    }
}

private data class VisibleDiceSurface(
    val surface: DiceSurface3D?,
    val points: List<DiceVector>,
    val normal: DiceVector,
    val center: DiceVector,
    val emission: Color? = null,
)

internal fun DrawScope.drawDice3D(
    number: Int, progress: Float, variant: Int, skin: DiceSkinStyle, hat: DiceHat, phase: Float,
) {
    if (number !in 1..6) return
    val p = progress.coerceIn(0f, 1f)
    val dimension = size.minDimension
    val scale = dimension * DICE_PROJECTION_SCALE
    // A little lift during the throw; the contact shadow stays on the table beneath it.
    val lift = sin(p * PI).toFloat() * dimension * 0.035f
    val origin = Offset(size.width * 0.5f, size.height * 0.47f - lift)
    // Calculate the rotation basis once per frame, rather than doing trigonometry per vertex.
    val right = orientDiceVector(DiceVector(1f, 0f, 0f), number, p, variant)
    val down = orientDiceVector(DiceVector(0f, 1f, 0f), number, p, variant)
    val forward = orientDiceVector(DiceVector(0f, 0f, 1f), number, p, variant)
    fun orient(point: DiceVector) = right * point.x + down * point.y + forward * point.z
    fun project(point: DiceVector): Offset {
        val (x, y) = projectDiceVector(point)
        return origin + Offset(x * scale, y * scale)
    }
    fun polygon(points: List<DiceVector>) = Path().apply {
        points.forEachIndexed { index, point ->
            val screen = project(point)
            if (index == 0) moveTo(screen.x, screen.y) else lineTo(screen.x, screen.y)
        }
        close()
    }

    // Layered translucent ovals approximate a soft contact shadow without platform blur APIs.
    for (layer in 8 downTo 1) {
        val width = dimension * (0.57f + layer * 0.028f + lift / dimension * 0.5f)
        val height = dimension * (0.075f + layer * 0.014f)
        drawOval(
            Color.Black.copy(alpha = (0.014f + p * 0.006f)),
            topLeft = Offset((size.width - width) * 0.5f, dimension * 0.88f - height * 0.5f),
            size = Size(width, height),
        )
    }

    val camera = DiceVector(0f, 0f, DICE_CAMERA_DISTANCE)
    val light = DiceVector(-0.45f, -0.65f, 1f).normalized()
    val surfaces = diceSurfaces3D.map { surface ->
        val points = surface.vertices.map(::orient)
        VisibleDiceSurface(surface, points, orient(surface.normal),
            points.reduce { a, b -> a + b } * (1f / points.size))
    }.filter { it.normal.dot(camera - it.center) > 0f }.toMutableList()

    // Crossed flame ribbons rise from the rim of the actual top/result face. They rotate with
    // the cube and participate in depth sorting, so the cube occludes flames on its far side.
    hat.palette?.let { palette ->
        val top = diceFaces3D.single { it.number == number }
        val topScreenY = project(orient(top.normal)).y
        for (index in 0 until 12) {
            val angle = index * (2f * PI / 12).toFloat()
            val u = cos(angle) * 0.67f
            val v = sin(angle) * 0.67f
            val anchor = top.point(u, v)
            // Keep the near-rim flames low, leaving the result pips unobscured.
            val nearRim = project(orient(anchor)).y > topScreenY
            val height = (if (nearRim) 0.22f else 1.1f) *
                (0.8f + 0.2f * sin(phase * 5f + index * 2.1f))
            for (cross in 0..1) for (core in 0..1) {
                val axis = if (cross == 0) top.across else top.down
                val width = if (core == 0) 0.115f else 0.047f
                val h = height * if (core == 0) 1f else 0.65f
                val sway = sin(phase * 3f + index) * 0.11f
                val points = listOf(
                    anchor - axis * width,
                    anchor - axis * (width * 0.7f) + top.normal * (h * 0.45f),
                    anchor + axis * sway + top.normal * h,
                    anchor + axis * width + top.normal * (h * 0.3f),
                    anchor + axis * width,
                ).map(::orient)
                val color = if (core == 0) palette.diceFlameColor else palette.diceFlameCore
                surfaces.add(VisibleDiceSurface(null, points, DiceVector(0f, 0f, 0f),
                    points.reduce { a, b -> a + b } * (1f / points.size),
                    color.copy(alpha = if (nearRim) 0.28f else if (core == 0) 0.55f else 0.8f)))
            }
        }
    }

    surfaces.sortedBy { it.center.z }.forEach { visible ->
        val path = polygon(visible.points)
        visible.emission?.let { color ->
            val points = visible.points.map(::project)
            val flame = Path().apply {
                moveTo(points[0].x, points[0].y)
                quadraticBezierTo(points[1].x, points[1].y, points[2].x, points[2].y)
                quadraticBezierTo(points[3].x, points[3].y, points[4].x, points[4].y)
                close()
            }
            drawPath(flame, color.copy(alpha = color.alpha * 0.12f),
                style = Stroke(width = dimension * 0.055f))
            drawPath(flame, color)
            return@forEach
        }
        val diffuse = visible.normal.dot(light).coerceIn(0f, 1f)
        val shine = diffuse.pow(18) * 0.14f
        val brightness = (0.56f + diffuse * 0.4f).coerceAtMost(1f)
        val surface = checkNotNull(visible.surface)
        val color = lerp(Color.Black, skin.surfaceColors[skin.surfaceColors.size / 2], brightness)
        drawPath(path, color)
        // Cover hairline antialias seams between adjoining bevel strips with the same material.
        drawPath(path, color, style = Stroke(width = 0.65f))

        val face = surface.face
        if (face != null && skin.surfaceColors.size > 1) {
            drawPath(path, Brush.linearGradient(skin.surfaceColors,
                start = project(orient(face.point(-0.78f, -0.78f))),
                end = project(orient(face.point(0.78f, 0.78f)))))
            drawPath(path, Color.Black.copy(alpha = 1f - brightness))
        }
        if (shine > 0.01f) drawPath(path, Color.White.copy(alpha = shine))
        if (face == null) return@forEach
        drawPath(path, Brush.verticalGradient(
            listOf(Color.White.copy(alpha = 0.12f), Color.Transparent, Color.Black.copy(alpha = 0.035f)),
            startY = origin.y - scale, endY = origin.y + scale,
        ))
        skin.innerFire?.let { palette ->
            clipPath(path) {
                // Surface flames are projected in each face's own coordinates, never screen-fixed.
                for (index in 0 until 11) {
                    val u = -0.76f + index * 0.15f
                    val height = 0.55f + 0.8f * (0.5f + 0.5f * sin(index * 2.3f + phase * 4f))
                    val sway = sin(phase * 3f + index) * 0.13f
                    for (core in 0..1) {
                        val w = if (core == 0) 0.14f else 0.065f
                        val h = height * if (core == 0) 1f else 0.6f
                        val flame = polygon(listOf(
                            face.point(u - w, 0.8f), face.point(u - w, 0.8f - h * 0.35f),
                            face.point(u + sway, 0.8f - h), face.point(u + w, 0.8f - h * 0.25f),
                            face.point(u + w, 0.8f),
                        ).map(::orient))
                        drawPath(flame, (if (core == 0) palette.diceFlameColor else palette.diceFlameCore)
                            .copy(alpha = if (core == 0) 0.78f else 0.72f))
                    }
                }
            }
        }
        for ((u, v) in dicePips3D(face.number)) {
            fun pip(radius: Float, downShift: Float = 0f): Path = polygon(
                (0 until 24).map { step ->
                    val angle = step * (2 * PI / 24).toFloat()
                    orient(face.point(u + cos(angle) * radius, v + sin(angle) * radius + downShift))
                },
            )
            // Dark sockets keep even white pips legible over bright gradients and moving fire.
            drawPath(pip(0.155f), Color.Black.copy(alpha = 0.7f))
            drawPath(pip(0.132f), skin.pipColor3D)
            drawPath(pip(0.105f, 0.012f), lerp(skin.pipColor3D, Color.White, 0.10f))
        }
    }
}

private val FirePalette.diceFlameColor: Color
    get() = if (this == FirePalette.BLUE) Color(0xFF159EFF) else Color(0xFFFF650A)
private val FirePalette.diceFlameCore: Color
    get() = if (this == FirePalette.BLUE) Color(0xFFA6F1FF) else Color(0xFFFFDC70)
